// What you are holding, grouped — the todo list (Phase 3.5).
//
// The held surface was a flat, newest-first list of everything not trashed:
// unrouted inbox items, Menu items and completed work all mixed together with no
// way to tell them apart. At twenty items that is the pile this app exists to
// stand between you and. Worse, a completed item keeps the gate's cure clock, so
// it rendered as "returns today" while being finished — copy the data does not
// support.
//
// Groups are COMPUTED, never stored, in a fixed order. Nothing here is a score:
// there are no counts of things you did not do, no streaks, and Done sits last
// and quiet because it is a record, not a reward (law 5).
//
// PURE. `now` and `zone` are arguments, like everywhere else.

import { isAppClock, type NodeState, type State } from './fold.ts';
import { heldWork } from './gate.ts';
import { isReadyAgain, pressureOf } from './pressure.ts';
import { raisesReplanCard } from './replan.ts';
import { calendarDaysBetween, isValidIso, type DayShape } from './time.ts';
import { boundaryOf } from './day.ts';
import { isGone, isHeld } from './fold.ts';
import { nodeWords } from './kind-words.ts';
import type { NodeKind } from './events.ts';

export type HeldGroupKey = 'unsorted' | 'replan' | 'ready' | 'soon' | 'later' | 'menu' | 'done';

export interface HeldGroup {
  key: HeldGroupKey;
  /** Plain words, and the only channel the grouping depends on (B-01). */
  title: string;
  items: NodeState[];
}

/** How far out "Coming up" reaches before something is simply "Later". A week is
 *  the horizon a person can actually hold in their head. */
export const SOON_DAYS = 7;

/**
 * The soonest demanding clock — the CLOCK, not just its distance.
 *
 * Returning only a day count was a real defect: the status line then picked a
 * clock of its own (the first in insertion order) and printed ITS date, so a card
 * grouped on a due date nine days out could print the review date four hundred
 * days out. Grouping and words must name the same clock or the card states a
 * date the data does not support (Doctrine §5) — the exact class ADR-0032 exists
 * to have fixed.
 *
 * `park` is excluded from DEMAND: a parked thing is being held away from you on
 * purpose, so it must not make something "Ready now". It is reported separately.
 */
function soonestDemand(n: NodeState, day: DayShape, nowIso: string): { days: number; at: string } | null {
  // A LIVE DECLINE, STILL PARKED AHEAD, DEMANDS NOTHING.
  //
  // Reported from use, 2026-08-09: route something to waiting-for, open it,
  // press "Not mine to carry" — and it stays in "Ready now" while the
  // confirmation promises nothing will chase you. Two causes, and the park
  // interval was only one. Routing CURES the node with a review clock, and a
  // cure inherits the intent of the event it cured: `gate:clarify.routed` is a
  // demand, because somebody did something. So the row stayed ready on the
  // strength of a clock set before the decline existed, and the decline had no
  // way to answer it.
  //
  // Answered here rather than by clearing clocks at the write site: clearing
  // would destroy a real date somebody had set, which is a defect class this
  // repo has already paid for. And a projection fix repairs everything ALREADY
  // declined, where a write-site fix only helps the next one.
  //
  // ONLY WHILE THE PARK IS STILL AHEAD. Once it comes round, the existing rule
  // takes over and the row reads as back with you — that is ADR-0056's "comes
  // back on its own day", and 1.30.x has a test that says so. This quiets a
  // decline; it does not make one permanent.
  //
  // `notNow` is the right marker because the fold clears it on
  // `clock.cleared{park}` and `done.marked` — so "Carry it after all" undoes
  // this in the same instant.
  if (n.notNow) {
    const p = n.clocks.park;
    if (p && isValidIso(p.at) && calendarDaysBetween(nowIso, p.at, day) > 0) return null;
  }

  // App clocks excluded too, and for the same reason `park` is: a gate cure is not
  // a demand. It exists so nothing goes silent, and reading it as "ready" made
  // every undated thing claim a place in today.
  const demand = soonestClock(n, day, nowIso, false, false);
  if (demand) return demand;

  // A PARK THAT HAS COME ROUND IS NO LONGER HOLDING ANYTHING AWAY.
  //
  // The exclusion above is right while the park is in the future — that is the
  // whole meaning of parking something, and ADR-0056 says a declined request
  // "comes back on its own day, quietly". The day arriving is when it stops
  // being held away, and nothing noticed: verified by running the fold, a park
  // whose returnAt had passed by a month read "back now" while sitting in
  // "Later" — the group for things with nothing asking — offered nowhere and
  // raising no card. The ledger's own promise, unkept, for every declined
  // request and every routed worry the gate parks.
  //
  // QUIETLY IS THE WORD THAT MATTERS. This moves the row into the group that
  // means something is back; it does NOT make it an offer. `nextup.ts` still
  // excludes park from `arrivedClock`, because putting a thing you declined at
  // the top of the work surface is the app not believing you — which is the one
  // failure the Not Now ledger exists to prevent.
  const park = n.clocks.park;
  if (!park || !isValidIso(park.at)) return null;
  const days = calendarDaysBetween(nowIso, park.at, day);
  return days <= 0 ? { days, at: park.at } : null;
}

/**
 * The soonest clock that will bring this node back, compared as INSTANTS.
 *
 * `includePark` is why this is one function rather than two. A park is not a
 * DEMAND — it is held away from you on purpose, so it must never make something
 * "Ready now" — but it IS a return date, and anything asking "when does this come
 * back" (the calendar) must count it. `src/ics.ts` had its own copy that excluded
 * park, so an item the list displayed as "parked until Aug 1" was silently
 * missing from the calendar: the app contradicting itself, which is precisely
 * what ADR-0032 claims one definition prevents.
 *
 * `Date.parse`, never a string compare. That duplicate sorted `c.at`
 * LEXICOGRAPHICALLY, which matches instant order only while every stored
 * timestamp is a Z-suffixed ISO string of identical shape — and nothing enforces
 * that, since `isValidIso` accepts any format `Date.parse` handles and import
 * validates none. An offset-form timestamp made the card and the calendar name
 * different days for the same node (audit).
 */
export function soonestClock(
  n: NodeState, day: DayShape, nowIso: string, includePark: boolean,
  /**
   * Count clocks the APP set (gate cures)?
   *
   * `false` when deciding whether something is ready or coming up, because a cure
   * says "this will come back to you", not "this is due". With cures counted, every
   * dateless thing sat in "Ready now" — a real import of 1,429 items and the heading
   * claimed 1,055 were ready today, which was arithmetically true and completely
   * false as a statement about anybody's day.
   *
   * `true` when the question is "does this have any clock at all", which is what
   * law 1 and the parked-until line are about.
   */
  includeAppClocks = true,
): { days: number; at: string } | null {
  let best: { days: number; at: string; ms: number } | null = null;
  for (const c of Object.values(n.clocks)) {
    if (!c || !isValidIso(c.at)) continue;
    if (!includePark && c.kind === 'park') continue;
    if (!includeAppClocks && isAppClock(c)) continue;
    const ms = Date.parse(c.at);
    if (best === null || ms < best.ms) {
      best = { days: calendarDaysBetween(nowIso, c.at, day), at: c.at, ms };
    }
  }
  return best ? { days: best.days, at: best.at } : null;
}

/** A park is not a demand, but it is still a return, and the surface should be
 *  able to say so rather than calling a thing coming back tomorrow "held". */
function parkedUntil(n: NodeState): string | null {
  const p = n.clocks.park;
  return p && isValidIso(p.at) ? p.at : null;
}

/** A date a year or more away must say which year. "Sep 1" for 2036 is
 *  indistinguishable from this September, in an app whose whole job is telling
 *  you when something comes back. */
function dateWords(at: string, zone: string, days: number): string {
  return new Date(at).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    ...(Math.abs(days) >= 365 ? { year: 'numeric' } : {}),
    timeZone: zone,
  });
}

/**
 * THE DAY A CLOCK NAMES, in the words the rest of the app already uses for one.
 *
 * Extracted rather than written: the vocabulary below was the tail of
 * `heldStatus` and nowhere else, so a second surface wanting "tomorrow" had to
 * either call the whole-node status — which answers a different question — or
 * print the raw key. The detail sheet did the second, and a thing's own page
 * said `comes back 2026-09-09` while the held card and the coverage sheet said
 * *tomorrow* about the same date. `heldStatus` now calls this, so there is one
 * definition and the two cannot drift.
 */
export function clockDayWords(at: string, nowIso: string, zone: string, day: DayShape): string {
  const days = calendarDaysBetween(nowIso, at, day);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  // `<=`, matching the group boundary exactly.
  if (days > 1 && days <= SOON_DAYS) return `in ${days} days`;
  return dateWords(at, zone, days);
}

/**
 * WHAT IS COMING BACK, AND WHEN THE NEAREST OF IT IS — or null when nothing is.
 *
 * For the one surface that had no way to say it. Next up correctly offers nothing
 * when nothing has arrived, and its empty branch already names the UNDATED things
 * ("N things are here without a date") because a screen reading *nothing here*
 * over a full store is entry 3 of `docs/nd-collisions.md` rendered by the app —
 * the best-evidenced entry in the catalog and this product's thesis. What that
 * branch could not say is anything about work that IS dated and is not due today.
 *
 * A cold read walked into the gap: seven things sorted as *Next action*, which
 * takes tomorrow's clock by design, and the offer said *Nothing is asking today*
 * over a sentence counting five OTHER things. The seven were covered, were
 * returning, and appeared nowhere. Entry 3's own record of the same shape is
 * 2.8.1, which moved the coverage gauge out of the held list on the finding that
 * "a surface that goes quiet the moment it is out of sight commits the failure
 * this entry describes".
 *
 * THE CLOCK IS NOT TOUCHED, and that is the point of putting this here rather
 * than making next-actions available today. Sorting is not doing — `clarify:
 * next-action` dates tomorrow deliberately, so a triage run does not turn into a
 * work session — and the reader's own expectation that the seven would be offered
 * is an expectation, not a finding. What was wrong was the SILENCE, not the date.
 *
 * NOT A SECOND "WHAT IS COMING" PROJECTION. `datedDays` is the calendar-aligned
 * one and must stay identical to the exported file (`ics.ts` says so, and says
 * what re-deriving it cost in 0.9.0); this reads the `soon` bucket `heldGroups`
 * already computes and already draws a heading for. It adds no definition.
 */
export function comingBack(
  state: State, nowIso: string, zone: string,
): { count: number; words: string } | null {
  const day: DayShape = { zone, boundary: boundaryOf(state) };
  const soon = heldGroups(state, nowIso, zone).find(g => g.key === 'soon');
  if (!soon || soon.items.length === 0) return null;
  let nearest: string | null = null;
  for (const n of soon.items) {
    const d = soonestDemand(n, day, nowIso);
    if (!d) continue;
    if (nearest === null || d.at < nearest) nearest = d.at;
  }
  if (nearest === null) return null;
  return { count: soon.items.length, words: clockDayWords(nearest, nowIso, zone, day) };
}

/**
 * DATED, AND FURTHER OUT THAN THE WEEK — or null when nothing is.
 *
 * The third fact the empty offer needs, and the SIXTH cold read's third untrue
 * statement. *See what is next* said "9 things come back to you … 10 things are
 * here without a date" while *What comes back, and when* said "12 with a day",
 * and three dated projects appeared in neither of the first screen's numbers.
 *
 * The cause is a bucket, not a count. `comingBack` reads the `soon` group, which
 * is `1 <= days <= SOON_DAYS` BY CONSTRUCTION, and `undatedCount` asks whether
 * `soonestDemand` is null. Anything dated further out than a week satisfies
 * neither: it is not coming back soon and it is not undated, so the screen stood
 * over it in silence. Reproduced as arithmetic on a store of five — two named,
 * three named nowhere.
 *
 * That silence is entry 3 of `docs/nd-collisions.md` — the best-evidenced entry
 * in the catalog and this product's thesis — committed by the app, and it is the
 * same shape as the fifth cold read's wall one release earlier: work that IS
 * dated, is not due today, and is named on no surface. The remedy there was the
 * sentence rather than the clock, and it is the sentence here too.
 *
 * STATED AS A FACT, AND IT ASKS FOR NOTHING. A date two months out must not
 * arrive with a day's weight on it; the reader is told what is held and when the
 * nearest of it is, and nothing more.
 *
 * Reads the `later` group `heldGroups` already computes rather than re-deriving,
 * and splits it on the one question that distinguishes its two halves: does this
 * carry a clock somebody actually set. `isAppClock` is that question everywhere
 * else in the app, so the split cannot drift from the one the offer's other
 * sentence makes.
 */
export function furtherOut(
  state: State, nowIso: string, zone: string,
): { count: number; words: string } | null {
  const day: DayShape = { zone, boundary: boundaryOf(state) };
  const later = heldGroups(state, nowIso, zone).find(g => g.key === 'later');
  if (!later) return null;
  let count = 0;
  let nearest: string | null = null;
  for (const n of later.items) {
    const d = soonestDemand(n, day, nowIso);
    if (!d) continue;                       // the undated half; the other sentence has it
    count += 1;
    if (nearest === null || d.at < nearest) nearest = d.at;
  }
  if (count === 0 || nearest === null) return null;
  return { count, words: clockDayWords(nearest, nowIso, zone, day) };
}

/**
 * Exactly one group per node in `heldWork` — the grouping is TOTAL over that
 * set, so the sum of the groups equals `coverageGauge(state).total` and equals
 * the coverage list's rows. The number, the claim it invites you to open, and
 * the list you actually work from cannot drift apart.
 *
 * **That sentence was false between 1.13.0 and 1.15.1**, and it is recorded
 * rather than quietly repaired: the exclusions were hand-written HERE and the
 * gauge did not have them, so a journal entry (ADR-0061) and later a pebble
 * (ADR-0065) were counted by the gauge and never rendered here. `heldWork` is
 * now the single definition, in `gate.ts` beside the gauge that reads it.
 *
 * `silent` is a different question and is deliberately NOT narrowed: it runs
 * over every node, because excluding a kind from a proof is how law 1 gets
 * defined away (the 1.3.1 merged-node finding).
 *
 * Order of the tests matters and is the design:
 *  - **Done first**, so a completed thing stops claiming it is coming back
 *    whatever clock the gate left on it.
 *  - **Menu next**, because a Menu item is demand-free by law and must never be
 *    filed under a heading that implies it is asking for something (law 6).
 *  - **Unsorted next**, because triage owns those and the list should say so
 *    rather than quietly mixing them in with decided work.
 *  - **Then a passed hard date**, which is a decision rather than a return, and
 *    must not be filed under a heading that says it is ready to be got on with.
 *  - Then by when it comes back.
 */
export function heldGroups(state: State, nowIso: string, zone: string): HeldGroup[] {
  // Whose day decides what has passed — the same shape the replan surface builds,
  // so the list and that surface cannot disagree about one item.
  const day: DayShape = { zone, boundary: boundaryOf(state) };
  const buckets: Record<HeldGroupKey, NodeState[]> = {
    unsorted: [], replan: [], ready: [], soon: [], later: [], menu: [], done: [],
  };

  for (const n of heldWork(state)) {
    // DONE, and not still running on a cadence. The unconditional version filed
    // a recurring upkeep that had come round again under "Done" while
    // `upkeepChips` was offering it as live work — one node, one screen, two
    // contradictory statements, which is the class this file's own header says
    // it exists to remove (audit). Asked of the same predicate `nextup.ts` uses,
    // so the list and the chip cannot disagree about whether a rhythm is over.
    if (n.lastDone && !isReadyAgain(pressureOf(n, nowIso, day))) { buckets.done.push(n); continue; }
    if (n.onMenu) { buckets.menu.push(n); continue; }
    if (n.captured && n.route === null) { buckets.unsorted.push(n); continue; }
    // A hard date that went by gets its OWN heading, and this is not cosmetic.
    // Filed under "Ready now" the rows read as ordinary work — the same items
    // the replan surface directly above is asking a different question about,
    // so one screen showed one item twice with two questions attached. That is
    // precisely what excluding them from Next-up prevents, relocated. They stay
    // in the list, because the sum of these groups is what the coverage gauge
    // counts; only the heading changes, so the list and the surface agree.
    //
    // `raisesReplanCard` is the WHOLE question — eligibility and a passed clock.
    // Asking only about the clock made this agree with the replan surface by
    // accident of branch order, and the branch above has just changed, which is
    // exactly how that kind of agreement breaks silently.
    if (raisesReplanCard(n, nowIso, day)) { buckets.replan.push(n); continue; }
    const soon = soonestDemand(n, day, nowIso);
    if (soon === null) { buckets.later.push(n); continue; }   // held, but nothing asking
    if (soon.days <= 0) { buckets.ready.push(n); continue; }
    if (soon.days <= SOON_DAYS) { buckets.soon.push(n); continue; }
    buckets.later.push(n);
  }

  // Newest first within a group — capture order is the only order this surface
  // claims, and ULIDs sort by time.
  const newestFirst = (a: NodeState, b: NodeState): number => (a.id < b.id ? 1 : -1);

  const ORDER: [HeldGroupKey, string][] = [
    ['unsorted', 'Not sorted yet'],
    // The same words as the surface above and the same words as the row's own
    // status, deliberately. Three different phrasings for one state is three
    // things to learn.
    ['replan', 'Needs a new plan'],
    ['ready', 'Ready now'],
    ['soon', 'Coming up'],
    ['later', 'Later'],
    ['menu', 'On the Menu'],
    ['done', 'Done'],
  ];
  return ORDER
    .map(([key, title]) => ({ key, title, items: buckets[key].sort(newestFirst) }))
    .filter(g => g.items.length > 0);   // an empty group is not a heading
}

/** The status line for one card, in words. Never a countdown, never a rebuke,
 *  and never a claim the data does not support — a finished thing says so
 *  instead of reporting the cure clock it happens to carry. */
export function heldStatus(n: NodeState, nowIso: string, zone: string, day: DayShape): string {
  // Same guard, same order, same reasons as `heldGroups` — the two must agree
  // node for node, and a status that disagreed with its own heading is the
  // defect ADR-0032 exists to have fixed.
  if (n.lastDone && !isReadyAgain(pressureOf(n, nowIso, day))) return 'done';
  if (n.onMenu) return 'on the Menu';
  if (n.captured && n.route === null) return 'not sorted yet';
  // A hard date that went by is not "ready now" — that phrasing invites doing it
  // now, which is the one answer the date already ruled out, and it is the same
  // words the list uses for something simply waiting. It is a DECISION, and the
  // replan surface is where it gets made (law 3). Asked of the identical
  // predicate that raises the card, so the two surfaces cannot describe one item
  // differently — and it asks the WHOLE question rather than relying on the
  // branches above to have filtered first.
  if (raisesReplanCard(n, nowIso, day)) return 'needs a new plan';
  // A RETURNED PARK KEEPS ITS OWN WORD. `soonestDemand` now counts a park whose
  // day has arrived, so the row moves into "Ready now" where a person will see
  // it — but "ready now" is the wrong sentence about a thing you declined. It
  // came BACK; whether it is ready is the question you are being handed, not an
  // answer the app should give you. Asked before the demand branch so the more
  // precise word wins, in the same order-matters way as every guard above.
  {
    const park = parkedUntil(n);
    if (park && calendarDaysBetween(nowIso, park, day) <= 0
      && soonestClock(n, day, nowIso, false, false) === null) return 'back now';
  }
  const soon = soonestDemand(n, day, nowIso);
  if (soon === null) {
    // Nothing is demanding it — but a park is still a return date, and saying
    // "held" about something coming back tomorrow tells the user less than the
    // app knows.
    const park = parkedUntil(n);
    if (!park) return 'held';
    const d = calendarDaysBetween(nowIso, park, day);
    if (d <= 0) return 'back now';
    if (d === 1) return 'parked until tomorrow';
    return `parked until ${dateWords(park, zone, d)}`;
  }
  const { days, at } = soon;
  // A future START names itself: "not before Sep 3" is a fact about a choice
  // the reader made (or imported), and "in 5 days" hides which kind of date is
  // talking — a due date asks, a start date merely opens (1.3.0, the defer
  // verb). Only when the start IS the soonest demand: a nearer due date is the
  // louder fact and keeps the generic words.
  const start = n.clocks.start;
  if (start && isValidIso(start.at) && start.at === at && days > 0) {
    // The tie goes to the DEADLINE: with a due or suspense at the same instant
    // (or sooner), "not before" would describe an obligation as a door opening
    // (audit — both dates on one day read as pure deferral).
    const harder = [n.clocks.due, n.clocks.suspense].some(c =>
      c && isValidIso(c.at) && Date.parse(c.at) <= Date.parse(start.at));
    if (!harder) return `not before ${days === 1 ? 'tomorrow' : dateWords(at, zone, days)}`;
  }
  if (days < 0) return 'ready now';
  // The rest is the day itself, and `clockDayWords` is where it lives now — one
  // definition, so the detail sheet and this cannot describe one date two ways.
  // (The boundary is `<=`, matching the group exactly: they disagreed by one
  // once, and the last day of "Coming up" printed a date instead of "in 7 days".)
  return clockDayWords(at, nowIso, zone, day);
}

/**
 * How many LIVE things sit directly under each node — computed once, so a card
 * can say what it contains without every card re-scanning the store.
 *
 * The point is disambiguation, not decoration. An OmniFocus import brings a
 * project's actions in parented to it, but the flat list drew them the same as a
 * loose inbox item — so a thing already filed under "Boy Scouts" looked exactly
 * like something still needing a home, and a backlog could not be processed
 * because nothing said which was which (found on device). A trashed or merged
 * child is not something a node holds, so it does not count.
 */
export function liveChildCounts(state: State): Map<string, number> {
  const counts = new Map<string, number>();
  for (const n of state.nodes.values()) {
    if (isGone(n) || !n.parent) continue;
    counts.set(n.parent, (counts.get(n.parent) ?? 0) + 1);
  }
  return counts;
}

/**
 * The title of the live container a node sits in, or null.
 *
 * A card uses it to say "in <project>", so an imported action that already
 * belongs somewhere does not read like loose work. A trashed or merged-away
 * parent confers no home and returns null — the node really is loose again.
 */
export function parentTitleOf(n: NodeState, state: State): string | null {
  if (!n.parent) return null;
  const p = state.nodes.get(n.parent);
  if (!isHeld(p)) return null;
  return p.title || '(untitled)';
}

/** How many of a place's contents a return card names before it stops naming
 *  them. Law 8 bounds what a return may show: coming back to a place after a
 *  fortnight must not be met with a wall, and three is enough to recognize what
 *  is in there without the card becoming the list. */
export const CONTENTS_CAP = 3;

/**
 * WHAT IS ACTUALLY IN THIS PLACE — the other half of "the place comes back, and
 * its contents come back with it".
 *
 * `docs/nd-collisions.md` entry 3, its top-ranked proposal, and the completion
 * of the promise 1.19.0's docblock made. 1.26.0 made a place able to come back
 * at all; a place that arrives saying only "7 under it" tells you a number and
 * makes you go looking to find out whether it is the number you cared about.
 * Entry 3 is cue-dependent prospective memory: filed means gone, and a count is
 * not a cue. A NAME is.
 *
 * Bounded and honest about it: the first few by the order they were put there,
 * then how many more. Never the whole list — a return card that unfolds into
 * everything you filed is the pile, arriving on a schedule.
 *
 * Completed things are left out. A place coming round to be looked in is about
 * what is still in it; naming what you already did would be a receipt, and a
 * receipt for work is the shape law 5 refuses.
 *
 * Null when there is nothing live inside — an empty place says nothing rather
 * than announcing its emptiness.
 */
export function contentsWords(
  state: State, place: NodeState, cap = CONTENTS_CAP,
): string | null {
  const inside: string[] = [];
  let total = 0;
  for (const n of state.nodes.values()) {
    if (n.parent !== place.id) continue;
    if (isGone(n) || n.lastDone) continue;
    total += 1;
    if (inside.length < cap) inside.push(n.title || '(untitled)');
  }
  if (total === 0) return null;
  const more = total - inside.length;
  const named = inside.join(', ');
  return more > 0 ? `Holding ${named} and ${more} more` : `Holding ${named}`;
}

/**
 * The one line a card shows about itself: WHAT IT IS, the container it is in,
 * and/or how many things are under it. Any of them, all of them, or none — a
 * loose action with no parent and no children returns null and reads as exactly
 * what it is. One definition, so the list and any test agree.
 *
 * WHAT IT IS COMES FIRST (2.4.0, ADR-0094), and it is the half that was missing.
 * Found on a device: nothing on a card said which KIND of thing it was, so a
 * project, a goal and an ordinary next action all read as todos. A project with
 * children said "7 under
 * it" — a number, not a name — and a project with none, a goal, an area and an
 * outcome said nothing whatever, so every row in the list drew identically.
 *
 * `nodeWords` returns null for `action`, which is why this reads unchanged for
 * the overwhelming majority of rows: the unmarked case stays unmarked, and only
 * the kinds a reader cannot otherwise tell from an action get named. It is
 * `kindWords` plus the one fact that supersedes a kind — an answered waiting-for
 * says **Arrived** rather than going on claiming somebody else has it.
 */
export function placeWords(n: NodeState, state: State, childCounts: Map<string, number>): string | null {
  const parts: string[] = [];
  const what = nodeWords(n);
  if (what) parts.push(what);
  const parent = parentTitleOf(n, state);
  if (parent) parts.push(`in ${parent}`);
  const kids = childCounts.get(n.id) ?? 0;
  if (kids > 0) parts.push(kids === 1 ? '1 under it' : `${kids} under it`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Held, but carrying no date anybody chose — the honest size of "you have not
 * decided about these yet".
 *
 * Exists because "nothing is asking" is true and unhelpful on its own. A real
 * import of 1,429 things, none of them dated, left the work surface with
 * nothing to offer — which reads as an empty app rather than as a full one waiting
 * on a decision. This is the number that makes the difference sayable.
 *
 * Over `heldWork`, not `heldNodes` (1.15.1). A pebble has no date BY
 * CONSTRUCTION — the gate refuses a clock on a demand-free kind — so counting
 * one here says "you have not decided about this yet" about the one kind in the
 * app there is nothing to decide about, and saying so is its whole purpose
 * (ADR-0014). A journal entry is the same shape.
 */
export function undatedCount(state: State, nowIso: string, zone: string): number {
  const day: DayShape = { zone, boundary: boundaryOf(state) };
  let n = 0;
  for (const node of heldWork(state)) {
    if (node.lastDone) continue;
    if (node.onMenu) continue;
    if (node.captured && node.route === null) continue;   // the inbox says this already
    if (soonestDemand(node, day, nowIso) === null) n++;
  }
  return n;
}

